import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import SLAClock from "@/components/SLAClock";

const CATEGORIES = [
    { id: "payment",   label: "Payment / Refund issue" },
    { id: "quality",   label: "Service quality" },
    { id: "no_show",   label: "No show" },
    { id: "safety",    label: "Safety concern" },
    { id: "behaviour", label: "Behaviour / Conduct" },
    { id: "other",     label: "Other" },
];


export default function DisputeDialog({ bookingId, open, onClose }) {
    const [existing, setExisting] = useState(null);
    const [category, setCategory] = useState("quality");
    const [details, setDetails] = useState("");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !bookingId) return;
        setLoading(true);
        api.get(`/bookings/${bookingId}/dispute`)
            .then((r) => setExisting(r.data && r.data.id ? r.data : null))
            .catch(() => setExisting(null))
            .finally(() => setLoading(false));
    }, [open, bookingId]);

    if (!open) return null;

    const submit = async () => {
        if (details.trim().length < 10) {
            toast.error("Please share at least 10 characters of detail.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post(`/bookings/${bookingId}/dispute`, {
                category,
                details: details.trim(),
            });
            toast.success("Dispute opened. Admin will review and reply within 24 hours.");
            setExisting(data.dispute);
            setDetails("");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="dispute-dialog"
            className="fixed inset-0 z-[80] bg-black/70 grid place-items-center p-4"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md rounded-2xl border border-red-700/40 bg-background p-6"
            >
                <div className="flex items-start justify-between mb-5">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-700/40 text-red-300 grid place-items-center">
                            <AlertTriangle size={18} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.26em] text-red-300/90">Report an issue</div>
                            <h3 className="font-serif text-xl text-foreground mt-1">Open a dispute</h3>
                        </div>
                    </div>
                    <button
                        data-testid="dispute-close"
                        onClick={onClose}
                        className="w-8 h-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5 grid place-items-center"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>

                {loading && <div className="text-xs text-muted-foreground">Loading…</div>}

                {!loading && existing && (
                    <div
                        data-testid="dispute-existing"
                        className="rounded-lg border border-amber-700/40 bg-amber-500/10 p-4"
                    >
                        <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300">
                                Dispute {existing.status?.replace("_", " ")}
                            </div>
                            {existing.status === "open" && (
                                <SLAClock startedAt={existing.created_at} compact />
                            )}
                        </div>
                        <div className="text-xs text-muted-foreground mb-2">
                            Category: <span className="text-foreground">{existing.category}</span>
                        </div>
                        <div className="text-sm text-foreground whitespace-pre-wrap">{existing.details}</div>
                        {existing.admin_notes && (
                            <div className="mt-3 pt-3 border-t border-white/10">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300 mb-1">Admin notes</div>
                                <div className="text-xs text-muted-foreground">{existing.admin_notes}</div>
                            </div>
                        )}
                        <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                            Only one open dispute per booking — admin team will reach out.
                        </div>
                    </div>
                )}

                {!loading && !existing && (
                    <>
                        <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2 block">
                            What happened?
                        </label>
                        <select
                            data-testid="dispute-category"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-foreground mb-4 focus:border-red-500/60 focus:outline-none"
                        >
                            {CATEGORIES.map((c) => (
                                <option key={c.id} value={c.id}>{c.label}</option>
                            ))}
                        </select>

                        <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2 block">
                            Tell us more
                        </label>
                        <textarea
                            data-testid="dispute-details"
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                            placeholder="What went wrong? Include dates and specifics so admin can act fast."
                            maxLength={1500}
                            rows={5}
                            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-red-500/60 focus:outline-none resize-none"
                        />
                        <div className="text-[10px] text-muted-foreground mt-1 text-right">
                            {details.length}/1500
                        </div>

                        <div className="mt-4 flex gap-3">
                            <button
                                data-testid="dispute-cancel"
                                onClick={onClose}
                                className="flex-1 px-4 py-3 rounded-full border border-white/10 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:bg-white/5 transition"
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="dispute-submit"
                                disabled={saving}
                                onClick={submit}
                                className="flex-1 px-4 py-3 rounded-full bg-red-600 text-white text-xs uppercase tracking-[0.22em] font-semibold hover:bg-red-500 disabled:opacity-50 transition"
                            >
                                {saving ? "Opening..." : "Open dispute"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
